import {
  Component, computed,
  effect, ElementRef,
  inject,
  model,
  OnInit,
  signal, ViewChild,
  WritableSignal
} from '@angular/core';
import {ApiCallService} from '../../../../services/api-call.service';
import { ErrorMessageService } from "../../../../services/error-message.service";
import {first, finalize} from "rxjs/operators";
import {Session} from "../../../../services/session.service";
import {RightMenuService} from "../../../../services/right-menu.service";

@Component({
  selector: 'app-team-management',
  templateUrl: './team-management.component.html',
  styleUrl: './team-management.component.scss',
  standalone: false
})
export class TeamManagementComponent implements OnInit {
  private readonly apiService: ApiCallService = inject(ApiCallService);
  private readonly errorMessageService: ErrorMessageService = inject(ErrorMessageService);
  private readonly session = inject(Session);
  private readonly rightMenuService = inject(RightMenuService);

  @ViewChild('addTeamMemberBtn') addTeamMemberBtn?: ElementRef;
  private el = inject(ElementRef); // Inject ElementRef

  totalMembersCount = model.required<number>();
  showAddTeamMemberForm: WritableSignal<boolean> = signal(false);
  isLoaded: WritableSignal<boolean> = signal(false);
  errorMessage: WritableSignal<string> = signal('');
  groups: WritableSignal<standardsGroup[]> = signal([]);
  memberGroups: WritableSignal<standardsGroup[]> = signal([]);
  members: WritableSignal<teamMember[]> = signal([]);
  // Track resend state per member
  private resending: WritableSignal<Set<string>> = signal(new Set<string>());
  private resendSuccess: WritableSignal<Set<string>> = signal(new Set<string>());
  membersPending = computed(() =>{
    const pending = this.members().filter(member => member.pending);
    //sort them by email
    pending.sort((a, b) => {
      return a.email.localeCompare(b.email);
    });
    return pending;
  });
  otherMembers = computed(() => {
    const otherMembers = this.members().filter(member => !member.pending);
    // sort other members by admin first, then by name
    otherMembers.sort((a, b) => {
      if (a.userRole !== b.userRole) {
        return b.userRole - a.userRole; // assuming higher number means higher role
      }
      return a.name.localeCompare(b.name);
    });
    return otherMembers;
  })

  constructor() {
    effect(() => {
      if(this.members().length > 0){
        this.totalMembersCount.set(this.members().length);
      }
    });
  }

  ngOnInit() {
    if(this.rightMenuService.activeComponent() === 'team-management'){
      this.toggleAddTeamMemberBtn();
    }
    this.getTeam();
  }

  getTeam() {
    this.isLoaded.set(false);
    this.apiService.getData('/getHeaderTeam/', null)
      .pipe(first())
      .subscribe(response => {
        this.isLoaded.set(true);
        if (response?.success && response?.data && response?.data[0].dataSet.standards) {
          this.getTeamMembers(response.data[0].dataSet.teamMembers);
          this.convertStandards(response.data[0].dataSet.standards);
        } else {
          if(response && response.dataError && Array.isArray(response.dataError) && response.dataError.length > 0) {
            this.errorMessageService.showErrorModal("There was a problem with loading the team. The server responded with: " + response.dataError[0].message, response.dataError);
            return;
          }
          else {
            this.errorMessageService.showErrorModal("There was a problem with loading the team. The server did not return any error messages. Please contact support.", []);
          }
        }
      }
    );
  }

  getTeamMembers(teamMembers ) {
    let members: teamMember[] = [];
    teamMembers
      .filter(member => member.requestKey !== this.session.getUser().userRequestKey)
      .forEach((member) => {
      const names = member.fullName ? member.fullName.split( ' ' ) : [ '', '' ];
      const firstName = names[ 0 ] || '';
      const lastName = names.slice( 1 ).join( ' ' ) || '';
      const teamMember = {
        name: member.fullName,
        firstName: firstName,
        lastName: lastName,
        requestKey: member.requestKey,
        avatar: member.avatar,
        userRole: member.userRole,
        pending: member.pending,
        email: member.email ? member.email : member.fullName,
        inviteDate: member.dateInvited ? new Date(member.dateInvited) : null,
      }
      members.push(teamMember);
    });

    this.members.set(members);
  }

  convertStandards(standards) {
    let groups: standardsGroup[] = [];
    //divide standards into groups
    standards.forEach( standard => {
      //if group doesn't exist, create it
      if( !groups.find(group => group.requestKey === standard.group.requestKey) ) {
        groups.push( {
          description: standard.group.description,
          intermediate: false,
          name: standard.group.name,
          requestKey: standard.group.requestKey,
          standards: [],
          checked: true
        });
      }
      const group = groups.find(group => group.requestKey === standard.group.requestKey);
      //convert standard
      let newStandard: standard = {
        checked: true,
        description: standard.standardDescription? standard.standardDescription: "",
        name: standard.standardTitle,
        requestKey: standard.requestKey
      }
      group.standards.push( newStandard );
    });
    this.groups.set(groups);
    this.memberGroups.set( [ ...groups]);
    this.isLoaded.set(true);
  }

  toggleAddTeamMemberBtn() {
    const isOpening = !this.showAddTeamMemberForm();

    this.showAddTeamMemberForm.update(value => !value );

    // Wait for the DOM to update (button to disappear/reappear)
    setTimeout(() => {
      if (isOpening) {
        // Form opened: Find the first input inside the new component and focus it
        // This ensures screen readers enter the form immediately
        const firstInput = this.el.nativeElement.querySelector('app-add-team-member textarea') as HTMLElement;
        firstInput?.focus();
      } else {
        // Form closed: Focus must return to the trigger button
        this.addTeamMemberBtn?.nativeElement.focus();
      }
    }, 0);
  }

  // Helper methods for template
  isResending(requestKey: string): boolean {
    return this.resending().has(requestKey);
  }

  wasResendSuccessful(requestKey: string): boolean {
    return this.resendSuccess().has(requestKey);
  }

  resendInvite(requestKey: string) {
    // mark as resending
    this.resending.update(prev => {
      const next = new Set(prev);
      next.add(requestKey);
      return next;
    });
    // clear any previous success flag for this member
    this.resendSuccess.update(prev => {
      const next = new Set(prev);
      next.delete(requestKey);
      return next;
    });

    this.apiService.postJsonData('/resendInvite/', { requestKey: requestKey })
      .pipe(first(), finalize(() => {
        // remove resending flag
        this.resending.update(prev => {
          const next = new Set(prev);
          next.delete(requestKey);
          return next;
        });
      }))
      .subscribe(response => {
        if(response && response.success){
          // update member invite date
          this.members.update( members => {
            const member = members.find( m => m.requestKey === requestKey );
            if( member ) {
              member.inviteDate = new Date();
            }
            return [ ...members ];
          });
          // show success feedback for a few seconds
          this.resendSuccess.update(prev => {
            const next = new Set(prev);
            next.add(requestKey);
            return next;
          });
          setTimeout(() => {
            this.resendSuccess.update(prev => {
              const next = new Set(prev);
              next.delete(requestKey);
              return next;
            });
          }, 3000);
        }
        else{
          if(response && Array.isArray(response.dataError) && response.dataError.length > 0) {
            this.errorMessageService.showErrorModal("There was a problem with resending the invite. The server responded with: " + response.dataError[0].message, response.dataError);
          }
          else{
            this.errorMessageService.showErrorModal("There was a problem with resending the invite. The server did not return any error messages. Please contact support.", []);
          }
        }
      })
  }
}

export interface standardsGroup {
  name: string,
  description: string,
  requestKey: string,
  standards: standard[],
  checked: boolean,
  intermediate: boolean
}
export interface standard {
  name: string,
  description: string,
  requestKey: string,
  checked: boolean
}

export interface teamMember {
  name: string,
  firstName: string,
  lastName: string,
  email: string,
  requestKey: string,
  avatar: string,
  userRole: number,
  pending: boolean,
  inviteDate: Date | null,
}
